import { Component, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { StatisticsTableComponent } from '../statistics-table/statistics-table';
import { YearlyStatisticsTableComponent } from '../yearly-statistics-table/yearly-statistics-table.component';
import { PerformanceMetric } from '../../../interfaces/statistics.interface';
import { MonthlySelectorComponent } from '../monthly-selector/monthly-selector.component';
import { AuthService } from '../../../services/auth.service';
import { DateService } from '../../../services/date.service';
import { CallCenterStatsService } from '../../../services/call-center-stats.service';
import { SalesStatsService } from '../../../services/sales-stats.service';
import { StatsAggregationService } from '../../../services/stats-aggregation.service';
import { CallCenterStatsData, UpdatePlanRequest as CcUpdatePlanRequest } from '../../../interfaces/call-center-stats.interface';
import { SalesStats, UpdatePlanRequest as SalesUpdatePlanRequest } from '../../../interfaces/sales-stats.interface';
import { User } from '../../../interfaces/auth.interface';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../shared/components/confirm-dialog/confirm-dialog';

@Component({
  selector: 'app-statistic-summary-wrapper',
  standalone: true,
  imports: [FormsModule, MatSnackBarModule, StatisticsTableComponent, YearlyStatisticsTableComponent, MonthlySelectorComponent],
  templateUrl: './statistic-summary-wrapper.component.html',
  styleUrls: ['./statistic-summary-wrapper.component.scss']
})
export class StatisticSummaryWrapperComponent implements OnInit {
  selectedMonth: string = '';
  selectedYear: number = 0;
  daysInMonth: number = 30;
  isKumMode: boolean = false;
  isYearlyView: boolean = false;
  isExporting: boolean = false;

  // Előre betöltött bulk statisztikák (CC mód) - userId kulccsal
  preloadedStatsMap: { [userId: number]: CallCenterStatsData[] } | null = null;

  // Előre betöltött bulk statisztikák (KÜM mód) - userId kulccsal
  preloadedKumStatsMap: { [userId: number]: SalesStats[] } | null = null;

  // Az összes CC/KÜM user (aktív + inaktív) - a bulk lekérdezéshez szükséges
  private allCcUsers: User[] = [];
  private allKumUsers: User[] = [];

  @ViewChild('summaryTable') summaryTableComponent?: StatisticsTableComponent;

  months = [
    'január', 'február', 'március', 'április', 'május', 'június',
    'július', 'augusztus', 'szeptember', 'október', 'november', 'december'
  ];

  colleagues: User[] = [];
  kumColleagues: User[] = [];

  colleaguesWithMetrics: {colleague: User, performanceMetrics: PerformanceMetric[]}[] = [];
  kumColleaguesWithMetrics: {colleague: User, performanceMetrics: PerformanceMetric[]}[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private dateService: DateService,
    private snackBar: MatSnackBar,
    private callCenterStatsService: CallCenterStatsService,
    private salesStatsService: SalesStatsService,
    private statsAggregationService: StatsAggregationService,
    private dialog: MatDialog
  ) {}

  get isCurrentUserCallCenterEmployee(): boolean {
    return this.authService.isCallCenterEmployee();
  }

  get isCurrentUserKum(): boolean {
    return this.authService.getCurrentUserRole() === 'kam';
  }

  /** Igaz, ha a user egyszerre CC és KÜM szerepkörrel is rendelkezik */
  get isCurrentUserBothCcAndKum(): boolean {
    const roles = this.authService.getCurrentUserRoles();
    return roles.includes('call_center') && roles.includes('kam');
  }

  get canEditPlan(): boolean {
    // Csak Adminisztrátor és Értékesítés támogatási vezető szerkeszthet Plan sorokat
    return this.authService.isAdmin() || this.authService.isSalesSupportManager();
  }

  /**
   * Éves összesítő táblázathoz az összes kolléga adatainak összeállítása
   */
  get yearlyAllColleaguesData(): { colleague: User, userId: number }[] {
    const source = this.isKumMode ? this.kumColleaguesWithMetrics : this.colleaguesWithMetrics;
    return source.map(c => ({ colleague: c.colleague, userId: c.colleague.id }));
  }

  ngOnInit() {
    // Kettős szerepkör (CC + KÜM): alapértelmezés CC mód, de a váltás engedélyezett
    if (this.isCurrentUserBothCcAndKum) {
      this.isKumMode = false;
    } else if (this.authService.isCallCenterEmployee()) {
      this.isKumMode = false;
    } else if (this.authService.getCurrentUserRole() === 'kam') {
      this.isKumMode = true;
    }
    
    this.initializeCurrentDate();
    this.updateDaysInMonth();
    this.loadColleagues();
  }

  private initializeCurrentDate() {
    const currentDateInfo = this.dateService.getCurrentMonthInfo();
    const currentMonth = currentDateInfo.month - 1;
    const currentYear = currentDateInfo.year;

    this.selectedMonth = this.months[currentMonth];
    this.selectedYear = currentYear;
    
    this.cdr.detectChanges();
  }

  private loadColleagues() {
    if (this.authService.isCallCenterEmployee()) {
      const currentUser = this.authService.getCurrentUser();
      if (currentUser) {
        const currentUserAsColleague: User = {
          id: currentUser.id!,
          username: currentUser.username,
          name: currentUser.name || currentUser.username,
          jobs: currentUser.jobs,
          jobNames: currentUser.jobNames || [],
          email: currentUser.email || '',
          created_at: currentUser.created_at || new Date().toISOString(),
          is_active: currentUser.is_active
        };
        
        this.colleagues = [currentUserAsColleague];

        this.colleaguesWithMetrics = [{
          colleague: currentUserAsColleague,
          performanceMetrics: []
        }];

        // Ha a user CC + KÜM kettős szerepkörrel rendelkezik, a KÜM listát is feltöltjük
        if (this.isCurrentUserBothCcAndKum) {
          this.kumColleagues = [currentUserAsColleague];
          this.kumColleaguesWithMetrics = [{
            colleague: currentUserAsColleague,
            performanceMetrics: []
          }];
        } else {
          this.kumColleagues = [];
        }

        // Az aktuális módhoz töltjük be a bulk statisztikákat
        if (!this.isKumMode) {
          this.loadBulkStats();
        } else {
          this.loadKumBulkStats();
        }
        return;
      }
    }
    
    if (this.authService.getCurrentUserRole() === 'kam') {
      const currentUser = this.authService.getCurrentUser();
      if (currentUser) {
        const currentUserAsColleague: User = {
          id: currentUser.id!,
          username: currentUser.username,
          name: currentUser.name || currentUser.username,
          jobs: currentUser.jobs,
          jobNames: currentUser.jobNames || [],
          email: currentUser.email || '',
          created_at: currentUser.created_at || new Date().toISOString(),
          is_active: currentUser.is_active
        };
        
        this.colleagues = [];
        this.kumColleagues = [currentUserAsColleague];
        
        this.kumColleaguesWithMetrics = [{
          colleague: currentUserAsColleague,
          performanceMetrics: []
        }];
        
        // KÜM mód: bulk betöltés (1 API kérés)
        this.loadKumBulkStats();
        return;
      }
    }
    
    this.authService.getAllUsers().subscribe({
      next: (users: User[]) => {
        // Az összes CC/KÜM usert tároljuk (is_active szűrő nélkül),
        // hogy az inaktív userek múltbeli adatait is meg tudjuk jeleníteni
        this.allCcUsers = users.filter(user =>
          user.jobs?.some(j => j === 'call_center')
        );
        this.allKumUsers = users.filter(user =>
          user.jobs?.some(j => j === 'kam')
        );

        // Alapértelmezés: csak aktív userek (a bulk betöltés után bővítjük inaktívakkal ha van adatuk)
        this.colleagues = this.allCcUsers.filter(u => u.is_active);
        this.kumColleagues = this.allKumUsers.filter(u => u.is_active);

        this.generateColleaguesWithMetrics();

        // CC módban bulk betöltés (1 API kérés), KÜM módban is bulk betöltés
        if (!this.isKumMode) {
          this.loadBulkStats();
        } else {
          this.loadKumBulkStats();
        }
      },
      error: (error) => {
        this.snackBar.open(
          `Hiba a munkatársak adatainak betöltésekor: ${error.message || error}`,
          'Bezárás',
          {
            duration: 8000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: ['error-snackbar']
          }
        );
      }
    });
  }

  private generateColleaguesWithMetrics() {
    if (!this.authService.isCallCenterEmployee()) {
      this.colleaguesWithMetrics = this.colleagues.map(colleague => ({
        colleague: colleague,
        performanceMetrics: []
      }));
    }

    this.kumColleaguesWithMetrics = this.kumColleagues.map(colleague => ({
      colleague: colleague,
      performanceMetrics: []
    }));
    // detectChanges a hívók feladata (loadBulkStats vagy explicit hívás)
  }

  private updateDaysInMonth() {
    const monthIndex = this.months.indexOf(this.selectedMonth);
    if (monthIndex !== -1) {
      this.daysInMonth = new Date(this.selectedYear, monthIndex + 1, 0).getDate();
    }
  }

  onMonthChange(month: string) {
    this.selectedMonth = month;
    this.isYearlyView = month === 'összes';
    if (!this.isYearlyView) {
      this.updateDaysInMonth();
    }
    this.generateColleaguesWithMetrics();

    // CC módban bulk újratöltés az új hónaphoz, KÜM módban is bulk
    if (!this.isKumMode && !this.isYearlyView) {
      this.loadBulkStats();
    } else if (this.isKumMode && !this.isYearlyView) {
      this.loadKumBulkStats();
    } else {
      this.cdr.detectChanges();
    }
  }

  onYearChange(year: number) {
    this.selectedYear = year;
    this.updateDaysInMonth();
    this.generateColleaguesWithMetrics();

    // CC módban bulk újratöltés az új évhez, KÜM módban is bulk
    if (!this.isKumMode) {
      this.loadBulkStats();
    } else {
      this.loadKumBulkStats();
    }
  }

  onModeChange(isKumMode: boolean) {
    // Kettős szerepkör (CC + KÜM): engedélyezett a váltás
    if (this.isCurrentUserBothCcAndKum) {
      this.isKumMode = isKumMode;
      if (!this.isKumMode) {
        this.preloadedKumStatsMap = null;
        this.loadBulkStats();
      } else {
        this.preloadedStatsMap = null;
        this.loadKumBulkStats();
      }
      return;
    }

    if (this.authService.isCallCenterEmployee()) {
      this.isKumMode = false;
      return;
    }
    
    if (this.authService.getCurrentUserRole() === 'kam') {
      this.isKumMode = true;
      return;
    }
    
    this.isKumMode = isKumMode;

    // Másik mód map-jét null-ozzuk, hogy a template ne renderelés idő előtt
    if (!this.isKumMode) {
      this.preloadedKumStatsMap = null;
      this.loadBulkStats();
    } else {
      this.preloadedStatsMap = null;
      this.loadKumBulkStats();
    }
  }

  onPlanDataSaved(event: any) {
    console.log('Plan data saved:', event);
  }

  isExportDisabled(): boolean {
    const source = this.isKumMode ? this.kumColleaguesWithMetrics : this.colleaguesWithMetrics;
    return source.length === 0;
  }

  onExportRequested() {
    if (this.isExportDisabled()) {
      return;
    }

    const source = this.isKumMode ? this.kumColleaguesWithMetrics : this.colleaguesWithMetrics;
    const userIds = source.map(item => item.colleague.id).filter((id): id is number => !!id);
    const month = this.isYearlyView ? null : this.months.indexOf(this.selectedMonth) + 1;

    if (month !== null && month <= 0) {
      return;
    }

    this.isExporting = true;
    this.cdr.detectChanges();

    this.statsAggregationService.exportSummary(userIds, this.selectedYear, month, this.isKumMode).subscribe({
      next: (blob) => {
        this.downloadReportFile(blob, month);
        this.isExporting = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.snackBar.open('Az összesítő exportálása sikertelen', 'OK', { duration: 4000 });
        this.isExporting = false;
        this.cdr.detectChanges();
      }
    });
  }

  private downloadReportFile(blob: Blob, month: number | null) {
    const prefix = this.isKumMode ? 'KUM' : 'CC';
    const fileName = month === null
      ? `${prefix}_osszesito_${this.selectedYear}.xlsx`
      : `${prefix}_osszesito_${this.selectedYear}_${String(month).padStart(2, '0')}.xlsx`;

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  private isMonthOnOrAfterCreatedAt(year: number, month: number, createdAt: string | null | undefined): boolean {
    if (!createdAt) return true;
    const created = new Date(createdAt);
    const createdYear = created.getFullYear();
    const createdMonth = created.getMonth() + 1;
    return year > createdYear || (year === createdYear && month >= createdMonth);
  }

  /**
   * Igaz, ha a CC statisztika tömbök között van legalább egy nem-nulla tényleges vagy terv érték.
   */
  private hasCcNonZeroData(stats: CallCenterStatsData[] | undefined): boolean {
    if (!stats || stats.length === 0) return false;
    const fields: (keyof CallCenterStatsData)[] = [
      'initiated_calls_actual', 'initiated_calls_plan',
      'calls_picked_up', 'target_person_reached',
      'listened_completely', 'callback', 'rejected', 'robinson_list',
      'no_answer', 'appointment_scheduled_actual', 'appointment_scheduled_plan',
      'property_insurance_appointment_actual', 'property_insurance_appointment_plan'
    ];
    return stats.some(week =>
      fields.some(field => {
        const val = week[field];
        return Array.isArray(val) && (val as number[]).some(v => v !== 0);
      })
      || (Array.isArray(week.notes) && week.notes.some(n => n != null && n.trim() !== ''))
    );
  }

  /**
   * Igaz, ha a KÜM statisztika tömbök között van legalább egy nem-nulla tényleges vagy terv érték, vagy megjegyzés.
   */
  private hasKumNonZeroData(stats: SalesStats[] | undefined): boolean {
    if (!stats || stats.length === 0) return false;
    const fields: (keyof SalesStats)[] = [
      'appointment_scheduled_actual', 'appointment_scheduled_plan',
      'succesfulfirstmeetings_actual', 'succesfulfirstmeetings_plan',
      'successfulassessments_actual', 'successfulassessments_plan',
      'successfulproposalpresentations_actual', 'successfulproposalpresentations_plan',
      'successfulclosures_actual', 'successfulclosures_plan',
      'property_insurance_appointment_actual', 'property_insurance_appointment_plan'
    ];
    return stats.some(week =>
      fields.some(field => {
        const val = week[field];
        return Array.isArray(val) && (val as number[]).some(v => v !== 0);
      })
      || (Array.isArray(week.notes) && week.notes.some(n => n != null && n.trim() !== ''))
    );
  }

  /**
   * Bulk statisztikák betöltése az összes kollégához egyetlen API kéréssel.
   * Az eredmény preloadedStatsMap-ba kerül, amit a template továbbít a gyerekeknek.
   */
  private loadBulkStats(): void {
    // Minden CC usert lekérdezünk (aktív + inaktív), hogy a múltbeli adatok is megjelenjenek
    const allUsers = this.allCcUsers.length > 0
      ? this.allCcUsers
      : this.colleaguesWithMetrics.map(c => c.colleague);
    const userIds = allUsers.map(u => u.id).filter(id => id > 0);

    if (userIds.length === 0) {
      this.preloadedStatsMap = {};
      this.cdr.detectChanges();
      return;
    }

    const monthNumber = this.months.indexOf(this.selectedMonth) + 1;
    if (monthNumber <= 0) {
      this.preloadedStatsMap = {};
      this.cdr.detectChanges();
      return;
    }

    this.callCenterStatsService.getMonthStatsBulk(userIds, monthNumber, this.selectedYear).subscribe({
      next: (statsMap) => {
        // Aktív userek a létrehozás hónapjától jelennek meg; inaktív userek csak ha van nem-nulla tényleges adatuk
        if (this.allCcUsers.length > 0) {
          const monthNumber = this.months.indexOf(this.selectedMonth) + 1;
          this.colleagues = this.allCcUsers.filter(user =>
            (user.is_active && this.isMonthOnOrAfterCreatedAt(this.selectedYear, monthNumber, user.created_at))
            || (!user.is_active && this.hasCcNonZeroData(statsMap[user.id]))
          );
          this.colleaguesWithMetrics = this.colleagues.map(colleague => ({
            colleague,
            performanceMetrics: []
          }));
        }
        this.preloadedStatsMap = statsMap;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Hiba a bulk statisztikák betöltésekor:', error);
        // Fallback: üres map, a gyerekek magától töltik be az adatokat
        this.preloadedStatsMap = {};
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Előre betöltött statisztikák lekérése egy adott felhasználóhoz.
   * null: még nem töltődött be (a gyerek maga tölti be)
   * CallCenterStatsData[] / SalesStats[]: betöltött adat (üres tömb ha nincs adat)
   */
  getPreloadedStatsForUser(userId: number): CallCenterStatsData[] | null {
    if (this.isKumMode) {
      // KÜM módban null-t adunk CC-hez, a KÜM adatokat külön kezeljük
      return null;
    }
    if (!this.preloadedStatsMap) return null;
    return this.preloadedStatsMap[userId] || [];
  }

  /**
   * Előre betöltött KÜM statisztikák lekérése egy adott felhasználóhoz.
   */
  getPreloadedKumStatsForUser(userId: number): SalesStats[] | null {
    if (!this.isKumMode) return null;
    if (!this.preloadedKumStatsMap) return null;
    return this.preloadedKumStatsMap[userId] || [];
  }

  /**
   * KÜM bulk statisztikák betöltése az összes KÜM kollégához egyetlen API kéréssel.
   */
  private loadKumBulkStats(): void {
    // Minden KÜM usert lekérdezünk (aktív + inaktív), hogy a múltbeli adatok is megjelenjenek
    const allUsers = this.allKumUsers.length > 0
      ? this.allKumUsers
      : this.kumColleaguesWithMetrics.map(c => c.colleague);
    const userIds = allUsers.map(u => u.id).filter(id => id > 0);

    if (userIds.length === 0) {
      this.preloadedKumStatsMap = {};
      this.cdr.detectChanges();
      return;
    }

    const monthNumber = this.months.indexOf(this.selectedMonth) + 1;
    if (monthNumber <= 0) {
      this.preloadedKumStatsMap = {};
      this.cdr.detectChanges();
      return;
    }

    this.salesStatsService.getMonthStatsBulk(userIds, monthNumber, this.selectedYear).subscribe({
      next: (statsMap) => {
        // Aktív userek a létrehozás hónapjától jelennek meg; inaktív userek csak ha van nem-nulla tényleges adatuk
        if (this.allKumUsers.length > 0) {
          const monthNumber = this.months.indexOf(this.selectedMonth) + 1;
          this.kumColleagues = this.allKumUsers.filter(user =>
            (user.is_active && this.isMonthOnOrAfterCreatedAt(this.selectedYear, monthNumber, user.created_at))
            || (!user.is_active && this.hasKumNonZeroData(statsMap[user.id]))
          );
          this.kumColleaguesWithMetrics = this.kumColleagues.map(colleague => ({
            colleague,
            performanceMetrics: []
          }));
        }
        this.preloadedKumStatsMap = statsMap;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Hiba a bulk KÜM statisztikák betöltésekor:', error);
        // Fallback: üres map, a gyerekek magától töltik be az adatokat
        this.preloadedKumStatsMap = {};
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Az aktuális módnak megfelelő kollégalista, kivéve a megadott targetId-t.
   * Ez kerül a content_copy menübe a céltáblázat header-jében.
   */
  getOtherColleagues(targetUserId: number): User[] {
    const source = this.isKumMode ? this.kumColleaguesWithMetrics : this.colleaguesWithMetrics;
    return source
      .map(c => c.colleague)
      .filter(c => c.id !== targetUserId);
  }

  /**
   * Megerősítő dialógus megnyitása, majd a tervszámok átmásolása forrás → cél.
   */
  onCopyPlanRequested(targetColleague: User, ev: { sourceUserId: number, sourceUsername: string }): void {
    const dialogData: ConfirmDialogData = {
      title: 'Tervszámok másolása',
      type: 'modify',
      message: `Biztosan átmásolja "${ev.sourceUsername}" tervszámait "${targetColleague.username}" ${this.selectedMonth} havi táblázatába? A meglévő tervszámok felülíródnak.`
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed === true) {
        this.copyPlansFromTo(ev.sourceUserId, targetColleague.id);
      }
    });
  }

  /**
   * Tervszámok átmásolása a forrás kollégától a célhoz a kiválasztott hónap heteire.
   * Részleges hetekben csak a hónapba eső napokat írjuk felül; a kilógó napokon a cél
   * meglévő tervszámai megmaradnak.
   */
  private copyPlansFromTo(sourceUserId: number, targetUserId: number): void {
    const monthIndex = this.months.indexOf(this.selectedMonth);
    if (monthIndex < 0) return;

    if (this.isKumMode) {
      this.copyPlansKum(sourceUserId, targetUserId, monthIndex);
    } else {
      this.copyPlansCc(sourceUserId, targetUserId, monthIndex);
    }
  }

  /**
   * Egy heti tömbből kiolvasott érték (vagy 0, ha hiányzik / túlcímzett).
   */
  private getDayValue(arr: number[] | undefined | null, dayIndex: number): number {
    if (!arr || dayIndex < 0 || dayIndex >= arr.length) return 0;
    return arr[dayIndex] || 0;
  }

  /**
   * Meghatározza, hogy egy adott hét adott napindex-e (0=hétfő..4=péntek)
   * a kiválasztott hónapba esik-e.
   */
  private isDayInSelectedMonth(weekNumber: number, dayIndex: number, monthIndex: number): boolean {
    // ISO hét: a hét csütörtöke határozza meg, melyik évhez tartozik.
    // A hétfő dátumát megkeressük: jan 4. mindig az 1. ISO hétben van.
    const jan4 = new Date(this.selectedYear, 0, 4);
    const jan4Day = (jan4.getDay() + 6) % 7; // 0=hétfő..6=vasárnap
    const week1Monday = new Date(this.selectedYear, 0, 4 - jan4Day);
    const targetDate = new Date(week1Monday);
    targetDate.setDate(week1Monday.getDate() + (weekNumber - 1) * 7 + dayIndex);
    return targetDate.getFullYear() === this.selectedYear && targetDate.getMonth() === monthIndex;
  }

  /**
   * CC tervszámok másolása.
   */
  private copyPlansCc(sourceUserId: number, targetUserId: number, monthIndex: number): void {
    const sourceStats = this.preloadedStatsMap?.[sourceUserId] || [];
    const targetStats = this.preloadedStatsMap?.[targetUserId] || [];

    if (sourceStats.length === 0) {
      this.showError('A forrás kollégának nincsenek tervszámai a kiválasztott hónapban.');
      return;
    }

    const monthNumber = monthIndex + 1;
    const weekNumbers = this.callCenterStatsService.getWeekNumbersForMonth(monthNumber, this.selectedYear);

    const requests = weekNumbers.map(weekNumber => {
      const src = sourceStats.find(s => s.week_number === weekNumber);
      const tgt = targetStats.find(s => s.week_number === weekNumber);

      const initiated: number[] = [];
      const appointment: number[] = [];
      const property: number[] = [];

      for (let d = 0; d < 5; d++) {
        const inMonth = this.isDayInSelectedMonth(weekNumber, d, monthIndex);
        if (inMonth) {
          initiated.push(this.getDayValue(src?.initiated_calls_plan, d));
          appointment.push(this.getDayValue(src?.appointment_scheduled_plan, d));
          property.push(this.getDayValue(src?.property_insurance_appointment_plan, d));
        } else {
          initiated.push(this.getDayValue(tgt?.initiated_calls_plan, d));
          appointment.push(this.getDayValue(tgt?.appointment_scheduled_plan, d));
          property.push(this.getDayValue(tgt?.property_insurance_appointment_plan, d));
        }
      }

      const req: CcUpdatePlanRequest = {
        user_id: targetUserId,
        week_number: weekNumber,
        year: this.selectedYear,
        initiated_calls_plan: initiated,
        appointment_scheduled_plan: appointment,
        property_insurance_appointment_plan: property
      };
      return this.callCenterStatsService.updatePlanValues(req);
    });

    if (requests.length === 0) {
      this.showError('Nincs feldolgozható hét a kiválasztott hónapban.');
      return;
    }

    forkJoin(requests).subscribe({
      next: () => {
        this.showSuccess('Tervszámok sikeresen átmásolva.');
        this.loadBulkStats();
      },
      error: (error) => {
        console.error('Hiba a tervszámok másolása közben:', error);
        this.showError('Hiba történt a tervszámok másolása közben. Lehet, hogy csak részben sikerült.');
        this.loadBulkStats();
      }
    });
  }

  /**
   * KÜM tervszámok másolása.
   */
  private copyPlansKum(sourceUserId: number, targetUserId: number, monthIndex: number): void {
    const sourceStats = this.preloadedKumStatsMap?.[sourceUserId] || [];
    const targetStats = this.preloadedKumStatsMap?.[targetUserId] || [];

    if (sourceStats.length === 0) {
      this.showError('A forrás kollégának nincsenek tervszámai a kiválasztott hónapban.');
      return;
    }

    const monthNumber = monthIndex + 1;
    const weekNumbers = this.salesStatsService.getWeekNumbersForMonth(monthNumber, this.selectedYear);

    const requests = weekNumbers.map(weekNumber => {
      const src = sourceStats.find(s => s.week_number === weekNumber);
      const tgt = targetStats.find(s => s.week_number === weekNumber);

      const appointment: number[] = [];
      const firstMeetings: number[] = [];
      const assessments: number[] = [];
      const proposals: number[] = [];
      const closures: number[] = [];

      for (let d = 0; d < 5; d++) {
        const inMonth = this.isDayInSelectedMonth(weekNumber, d, monthIndex);
        if (inMonth) {
          appointment.push(this.getDayValue(src?.appointment_scheduled_plan, d));
          firstMeetings.push(this.getDayValue(src?.succesfulfirstmeetings_plan, d));
          assessments.push(this.getDayValue(src?.successfulassessments_plan, d));
          proposals.push(this.getDayValue(src?.successfulproposalpresentations_plan, d));
          closures.push(this.getDayValue(src?.successfulclosures_plan, d));
        } else {
          appointment.push(this.getDayValue(tgt?.appointment_scheduled_plan, d));
          firstMeetings.push(this.getDayValue(tgt?.succesfulfirstmeetings_plan, d));
          assessments.push(this.getDayValue(tgt?.successfulassessments_plan, d));
          proposals.push(this.getDayValue(tgt?.successfulproposalpresentations_plan, d));
          closures.push(this.getDayValue(tgt?.successfulclosures_plan, d));
        }
      }

      const req: SalesUpdatePlanRequest = {
        user_id: targetUserId,
        week_number: weekNumber,
        year: this.selectedYear,
        appointment_scheduled_plan: appointment,
        succesfulfirstmeetings_plan: firstMeetings,
        successfulassessments_plan: assessments,
        successfulproposalpresentations_plan: proposals,
        successfulclosures_plan: closures
      };
      return this.salesStatsService.updatePlanValues(req);
    });

    if (requests.length === 0) {
      this.showError('Nincs feldolgozható hét a kiválasztott hónapban.');
      return;
    }

    forkJoin(requests).subscribe({
      next: () => {
        this.showSuccess('Tervszámok sikeresen átmásolva.');
        this.loadKumBulkStats();
      },
      error: (error) => {
        console.error('Hiba a KÜM tervszámok másolása közben:', error);
        this.showError('Hiba történt a tervszámok másolása közben. Lehet, hogy csak részben sikerült.');
        this.loadKumBulkStats();
      }
    });
  }

  private showSuccess(message: string): void {
    this.snackBar.open(`✅ ${message}`, 'Bezár', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['success-snackbar']
    });
  }

  private showError(message: string): void {
    this.snackBar.open(`❌ ${message}`, 'Bezár', {
      duration: 5000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['error-snackbar']
    });
  }
}